import { useBackendApi, useObjectDatesMapper, EventTask, useTimezoneService, useDateHelper } from 'nekst-api';

export interface AttendeeDto {
  email: string;
  fullName: string | null;
}

export interface EventPreviewResponse {
  title: string;
  description: string;
  location: string | null;
  startTime: Date | null;
  endTime: Date | null;
  isAllDayEvent: boolean;
  attendees: AttendeeDto[];
  organizer: AttendeeDto | null;
}

interface EventPreviewRequest {
  name: string;
  description?: string;
  location?: string;
  dueDate?: {
    date: string;
    time: Date | null;
    endTime: Date | null;
    timezone: string;
  };
  inviteRecipients: Array<{ personId: number }>;
  assignments: Array<{ personId: number }>;
  _version: number;
}

export function useEventPreviewService() {
  const nekstApi = useBackendApi();
  const timezoneService = useTimezoneService();

  const withTimeMapper = useObjectDatesMapper<EventPreviewResponse>(
    [],
    ['startTime', 'endTime']
  );

  const dateOnlyMapper = useObjectDatesMapper<EventPreviewResponse>(
    ['startTime'],
    []
  )

  const mapSingle = (item: any) => {
    if (item.endTime) {
      return withTimeMapper.mapSingle(item);
    } else {
      return dateOnlyMapper.mapSingle(item);
    }
  }

  const dateHelper = useDateHelper();

  const buildRequestData = (eventData: Partial<EventTask>): EventPreviewRequest => {
    const tz = eventData.dueDate?.timezone || timezoneService.getTimezone();

    return {
      name: eventData.name || '',
      description: eventData.description,
      location: eventData.location,
      dueDate: eventData.dueDate?.date ? {
        date: eventData.dueDate.date
          ? dateHelper.toString(eventData.dueDate.date)
          : null,
        time: eventData.dueDate.time,
        endTime: eventData.dueDate.endTime,
        timezone: tz,
      } : undefined,
      inviteRecipients: (eventData.inviteRecipients || []).map(recipient => ({
        personId: recipient.personId,
      })),
      assignments: (eventData.assignments || [])
        .filter(assignment => assignment.personId !== undefined)
        .map(assignment => ({
          personId: assignment.personId!,
        })),
      _version: eventData._version || 0,
    };
  };

  const getEventPreview = async (
    taskId: number,
    eventData: EventTask
  ): Promise<EventPreviewResponse> => {
    const requestData = buildRequestData(eventData);
    const response = await nekstApi.post(`/tasks/${taskId}/eventpreview`, requestData);
    return mapSingle(response);
  };

  const getEventPreviewForProject = async (
    projectId: number,
    eventData: Partial<EventTask>
  ): Promise<EventPreviewResponse> => {
    const requestData = buildRequestData(eventData);
    const response = await nekstApi.post(`/projects/${projectId}/eventpreview`, requestData);
    return mapSingle(response);
  };

  return {
    getEventPreview,
    getEventPreviewForProject,
  };
}
